import { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";

import { walkupPinStatus } from "./components/WalkupPinCell";
import { walkupPinByEvent } from "./queries";

const WALKUP_PIN_POLL_MS = 5000;
const MAX_TIMER_MS = 2147483647;
const SETTLED_STATUSES = ["revoked", "expired"];
const RETRYING_STATUSES = ["pending", "failed"];

/*
 * The entries list only ever appends older pages, so a PIN that is still being
 * generated would never change on screen. Re-read just those entries until they
 * settle; Apollo merges each result into the cached row the list renders.
 * A failed PIN is re-read too: the API keeps re-queuing it while Edge asks for
 * it, so it can still turn into a working PIN (the booking page does the same).
 * The list is also re-rendered when a shown PIN runs out, so its digits give
 * way to "Expired" instead of staying up on an idle screen.
 */
export default function useWalkupPinRefresh(events) {
  const client = useApolloClient();
  const [expiryTick, setExpiryTick] = useState(0);
  const unsettledIds = events
    .filter((event) => RETRYING_STATUSES.includes(walkupPinStatus(event.pinpadAccess)))
    .map((event) => event.id)
    .join(",");
  const nextExpiryMs = Math.min(
    Infinity,
    ...events
      .filter((event) => {
        const status = walkupPinStatus(event.pinpadAccess);
        return status && !SETTLED_STATUSES.includes(status);
      })
      .map((event) => new Date(event.pinpadAccess.validTo).getTime())
      .filter((validToMs) => !Number.isNaN(validToMs))
  );

  useEffect(() => {
    if (nextExpiryMs === Infinity) return undefined;

    const delay = Math.min(Math.max(nextExpiryMs - Date.now(), 0), MAX_TIMER_MS);
    const timeoutId = setTimeout(() => setExpiryTick((tick) => tick + 1), delay);

    return () => clearTimeout(timeoutId);
  }, [nextExpiryMs, expiryTick]);

  useEffect(() => {
    if (!unsettledIds) return undefined;

    const intervalId = setInterval(() => {
      unsettledIds.split(",").forEach((id) => {
        client
          .query({ query: walkupPinByEvent, variables: { id }, fetchPolicy: "network-only" })
          .catch((error) => console.error("Error refreshing walk-up PIN", error));
      });
    }, WALKUP_PIN_POLL_MS);

    return () => clearInterval(intervalId);
  }, [client, unsettledIds]);
}
